<script>
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";
</script>

<svelte:head>
    <title
        >dinglandia | Web Design & Development, Graphic Design & Branding</title
    >
    <meta
        name="description"
        content="We specialize in creating unique experiences through stellar UX & UI design, prefering a mobile first approach and rapid prototyping in the browser."
    />

    <meta
        property="og:title"
        content="Web Design & Development, Graphic Design & Branding"
    />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/services" />
    <meta
        property="og:description"
        content="We specialize in creating unique experiences through stellar UX & UI design, prefering a mobile first approach and rapid prototyping in the browser."
    />
    <meta property="og:type" content="business.business" />
    <meta
        property="og:image"
        content="https://dinglandia.design/og/services.jpg"
    />
</svelte:head>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new" name="logo"></div>
    <h1 class="flash">services</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#services" aria-label="Jump to services section">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Value Based Pricing -->
<section class="value-pricing" aria-labelledby="value-pricing-title">
    <div class="container">
        <div class="value-pricing__intro">
            <p class="value-pricing__eyebrow">Pricing strategy spotlight</p>
            <h2 id="value-pricing-title">
                Value-Based Pricing Interactive Guide
            </h2>
            <p>
                Explore how we help teams capture the full value they create by
                looking beyond cost-plus methods.
            </p>
        </div>

        <div class="value-pricing__grid">
            <div class="pricing-stack-wrapper">
                <p class="value-pricing__hint" aria-live="polite">
                    Tap each layer to learn more
                </p>
                <div
                    class="pricing-stack"
                    role="group"
                    aria-label="Value-based pricing stack"
                >
                    <button
                        type="button"
                        class="pricing-layer cost"
                        data-layer="cost"
                        aria-pressed="false"
                    >
                        Cost
                    </button>
                    <button
                        type="button"
                        class="pricing-layer markup"
                        data-layer="markup"
                        aria-pressed="false"
                    >
                        Markup
                    </button>
                    <button
                        type="button"
                        class="pricing-layer perceived-value"
                        data-layer="value"
                        aria-pressed="false"
                    >
                        Perceived Value
                    </button>

                    <span class="bracket cost-plus" aria-hidden="true">
                        <span class="bracket-label">Cost-Plus</span>
                    </span>
                    <span class="bracket value-based" aria-hidden="true">
                        <span class="bracket-label">Value-Based</span>
                    </span>
                </div>
            </div>

            <div class="info-section">
                <div class="info-box">
                    <div class="info-header">Component Details</div>
                    <div class="info-content">
                        <article class="info-item active" data-info="default">
                            <h3>Interactive Diagram</h3>
                            <p>
                                Each layer represents a key part of your pricing
                                story:
                            </p>
                            <ul>
                                <li>
                                    <strong style="color: #0d47a1;">Cost</strong
                                    > — The non-negotiable floor
                                </li>
                                <li>
                                    <strong style="color: #f7a600;"
                                        >Markup</strong
                                    > — Classic cost-plus logic
                                </li>
                                <li>
                                    <strong style="color: #1fbfad;"
                                        >Perceived Value</strong
                                    > — Where opportunity lives
                                </li>
                            </ul>
                        </article>

                        <article class="info-item" data-info="cost" hidden>
                            <h3>
                                <span
                                    class="color-badge"
                                    style="background:#1565C0;"
                                ></span>Cost
                            </h3>
                            <p>Your baseline expenses that must be covered:</p>
                            <ul>
                                <li>Materials, tooling, and production</li>
                                <li>Labor, collaborators, and fulfillment</li>
                                <li>Research and development investments</li>
                                <li>Fixed + variable operational overhead</li>
                            </ul>
                            <p class="info-note">
                                Pricing below this floor erodes sustainability.
                            </p>
                        </article>

                        <article class="info-item" data-info="markup" hidden>
                            <h3>
                                <span
                                    class="color-badge"
                                    style="background:#FFC107;"
                                ></span>Markup (Cost-Plus)
                            </h3>
                            <p>
                                Traditional pricing layers a fixed percentage on
                                top of costs:
                            </p>
                            <ul>
                                <li>Fast to calculate and defend internally</li>
                                <li>
                                    Ignores how different buyers perceive value
                                </li>
                                <li>
                                    Leaves margin on the table for premium
                                    buyers
                                </li>
                                <li>Applies the same story to every persona</li>
                            </ul>
                            <p class="info-note">
                                Reliable, but rarely differentiating.
                            </p>
                        </article>

                        <article class="info-item" data-info="value" hidden>
                            <h3>
                                <span
                                    class="color-badge"
                                    style="background:#2BBBAD;"
                                ></span>Perceived Value
                            </h3>
                            <p>
                                Price based on the transformation you deliver:
                            </p>
                            <ul>
                                <li>
                                    ROI, time saved, and measurable outcomes
                                </li>
                                <li>Confidence in your process + proof</li>
                                <li>Experience, polish, and support layers</li>
                                <li>Brand, emotion, and relationship equity</li>
                            </ul>
                            <p class="info-note">
                                The space between cost-plus and value-based is
                                untapped profit.
                            </p>
                        </article>
                    </div>
                </div>

                <div class="value-pricing__actions">
                    <a
                        href="#services"
                        class="btn btn-lg btn-custom value-pricing__cta"
                        >view services</a
                    >
                </div>
            </div>
        </div>

        <div class="value-pricing__takeaways">
            <h3>Key Takeaways</h3>
            <ul>
                <li>
                    Value-based pricing captures the entire story your work
                    delivers.
                </li>
                <li>
                    Cost-plus models cap potential by ignoring perceived value.
                </li>
                <li>
                    Messaging, social proof, and outcomes make the value gap
                    visible.
                </li>
                <li>
                    The gap between approaches represents untapped profit
                    potential.
                </li>
                <li>
                    Different audiences perceive different levels of value -
                    adapt the offer.
                </li>
            </ul>
        </div>
    </div>
</section>
<!-- //Value Based Pricing -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img src="/img/mountains/mtnBlue.svg" alt="" style="--offset:0.012" />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- .//New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>

        <div class="row">
            <div
                class="col-10 offset-1 col-md-8 offset-md-2 services-tabs-wrapper"
            >
                <div class="services-tabs" data-tabs>
                    <div
                        class="services-tabs__nav"
                        id="v-pills-tab"
                        role="tablist"
                        aria-orientation="vertical"
                    >
                        <button
                            class="pill-btn active"
                            id="v-pills-discover-tab"
                            data-tab-target="v-pills-discover"
                            type="button"
                            role="tab"
                            aria-controls="v-pills-discover"
                            aria-selected="true"
                        >
                            <svg class="icon icon-discover"
                                ><use xlink:href="#icon-discover"></use></svg
                            >
                            <p>Discover</p>
                        </button>
                        <button
                            class="pill-btn"
                            id="v-pills-design-tab"
                            data-tab-target="v-pills-design"
                            type="button"
                            role="tab"
                            aria-controls="v-pills-design"
                            aria-selected="false"
                        >
                            <svg class="icon icon-design"
                                ><use xlink:href="#icon-design"></use></svg
                            >
                            <p>Design</p>
                        </button>
                        <button
                            class="pill-btn"
                            id="v-pills-develop-tab"
                            data-tab-target="v-pills-develop"
                            type="button"
                            role="tab"
                            aria-controls="v-pills-develop"
                            aria-selected="false"
                        >
                            <svg class="icon icon-develop"
                                ><use xlink:href="#icon-develop"></use></svg
                            >
                            <p>Develop</p>
                        </button>
                        <button
                            class="pill-btn"
                            id="v-pills-devops-tab"
                            data-tab-target="v-pills-devops"
                            type="button"
                            role="tab"
                            aria-controls="v-pills-devops"
                            aria-selected="false"
                        >
                            <svg class="icon icon-devops"
                                ><use xlink:href="#icon-devops"></use></svg
                            >
                            <p>Devops</p>
                        </button>
                        <button
                            class="pill-btn"
                            id="v-pills-agentic-tab"
                            data-tab-target="v-pills-agentic"
                            type="button"
                            role="tab"
                            aria-controls="v-pills-agentic"
                            aria-selected="false"
                        >
                            <svg class="icon icon-robot"
                                ><use xlink:href="#icon-robot"></use></svg
                            >
                            <p>Agentic</p>
                        </button>
                    </div>
                    <div class="services-tabs__panels" id="v-pills-tabContent">
                        <div
                            class="services-tabs__panel active"
                            id="v-pills-discover"
                            role="tabpanel"
                            aria-labelledby="v-pills-discover-tab"
                            data-tab-panel="v-pills-discover"
                        >
                            <h3>Discover:</h3>
                            <ul class="custom-list">
                                <li>
                                    <strong>Consulting</strong>
                                    <ul>
                                        <li>
                                            Strategic guidance and expert advice
                                            to navigate business challenges
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Research</strong>
                                    <ul>
                                        <li>
                                            In-depth analysis to inform
                                            decision-making and strategy
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Target Audience</strong>
                                    <ul>
                                        <li>
                                            Identifying and understanding your
                                            ideal customer demographics
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Business Plan</strong>
                                    <ul>
                                        <li>
                                            Comprehensive strategy development
                                            for sustainable growth
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Industry Landscape</strong>
                                    <ul>
                                        <li>
                                            Market analysis and trend
                                            identification for competitive
                                            advantage
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Competitor Research</strong>
                                    <ul>
                                        <li>
                                            Deep-dive analysis of competitors to
                                            identify opportunities
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Technical Stack</strong>
                                    <ul>
                                        <li>
                                            Technology recommendation and
                                            architecture planning
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>3P tooling</strong>
                                    <ul>
                                        <li>
                                            Third-party tool selection and
                                            integration strategy
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div
                            class="services-tabs__panel"
                            id="v-pills-design"
                            role="tabpanel"
                            aria-labelledby="v-pills-design-tab"
                            data-tab-panel="v-pills-design"
                            hidden
                        >
                            <h3>Graphic Design:</h3>
                            <ul class="custom-list">
                                <li>
                                    <strong>Branding</strong>
                                    <ul>
                                        <li>
                                            Creating memorable brand identities
                                            that resonate with audiences
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Logo Design</strong>
                                    <ul>
                                        <li>
                                            Distinctive visual symbols that
                                            capture brand essence
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Graphic Design</strong>
                                    <ul>
                                        <li>
                                            Creative visual solutions for print
                                            and digital media
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Business Cards</strong>
                                    <ul>
                                        <li>
                                            Professional networking materials
                                            that make lasting impressions
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Album Covers</strong>
                                    <ul>
                                        <li>
                                            Eye-catching artwork that represents
                                            musical identity
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Labels/Stickers</strong>
                                    <ul>
                                        <li>
                                            Brand reinforcement through custom
                                            printed materials
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Flyers</strong>
                                    <ul>
                                        <li>
                                            Promotional materials designed to
                                            generate interest and drive action
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Posters</strong>
                                    <ul>
                                        <li>
                                            Large-format visuals for impactful
                                            messaging and brand awareness
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Digital Design</strong>
                                    <ul>
                                        <li>
                                            User-centered digital experiences
                                            across multiple platforms
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Design in the browser</strong>
                                    <ul>
                                        <li>
                                            Quick, interactive prototype
                                            development in the browser where you
                                            can find out what works and what
                                            doesn't very quickly as you are
                                            testing in the end product (the
                                            browser)
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Digital Advertisement</strong>
                                    <ul>
                                        <li>
                                            Engaging ad creative for online
                                            platforms and campaigns
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Landing Pages</strong>
                                    <ul>
                                        <li>
                                            Conversion-optimized pages with
                                            compelling copy and design
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Album Covers</strong>
                                    <ul>
                                        <li>
                                            Digital artwork for streaming
                                            platforms and online distribution
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div
                            class="services-tabs__panel"
                            id="v-pills-develop"
                            role="tabpanel"
                            aria-labelledby="v-pills-develop-tab"
                            data-tab-panel="v-pills-develop"
                            hidden
                        >
                            <h3>Web Development:</h3>
                            <ul class="custom-list">
                                <li>
                                    <strong>Rapid Prototyping</strong>
                                    <ul>
                                        <li>
                                            Fast iteration and testing of ideas
                                            to validate concepts quickly
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Progressive Web Applications</strong
                                    >
                                    <ul>
                                        <li>
                                            Modern web apps that offer
                                            native-like experience and offline
                                            functionality
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>React</strong>
                                    <ul>
                                        <li>
                                            Component-based UI library for
                                            building interactive interfaces
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Svelte</strong>
                                    <ul>
                                        <li>
                                            Modern, open-source JavaScript
                                            framework for building fast,
                                            reactive web applications
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Vue</strong>
                                    <ul>
                                        <li>
                                            Open-source, progressive JavaScript
                                            framework for building user
                                            interfaces and single-page
                                            applications, focusing on the view
                                            layer
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>TanStack</strong>
                                    <ul>
                                        <li>
                                            Collection of high-quality,
                                            framework-agnostic, and headless
                                            open-source libraries
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>WordPress</strong>
                                    <ul>
                                        <li>
                                            Decoupled content management
                                            leveraging familiar interface
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Astro</strong>
                                    <ul>
                                        <li>
                                            Open-source web framework for
                                            building fast, content-focused
                                            websites
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Shopify</strong>
                                    <ul>
                                        <li>
                                            leading e-commerce platform that
                                            provides tools for businesses to
                                            build, manage, and grow online
                                            stores
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Remix 3</strong>
                                    <ul>
                                        <li>
                                            Radical, non-backwards-compatible
                                            rebuild of the Remix web framework
                                            that abandons React, offering a
                                            faster, lightweight alternative
                                            built on native Web APIs
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div
                            class="services-tabs__panel"
                            id="v-pills-devops"
                            role="tabpanel"
                            aria-labelledby="v-pills-devops-tab"
                            data-tab-panel="v-pills-devops"
                            hidden
                        >
                            <h3>DevOps:</h3>
                            <ul class="custom-list">
                                <li>
                                    <strong>Cross Browser Testing</strong>
                                    <ul>
                                        <li>
                                            Ensuring consistent experience
                                            across all major web browsers
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Device & Platform Testing</strong>
                                    <ul>
                                        <li>
                                            Comprehensive testing across
                                            desktop, mobile, and tablet devices
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Launch Protocols</strong>
                                    <ul>
                                        <li>
                                            Streamlined deployment processes for
                                            reliable product launches
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Netlify</strong>
                                    <ul>
                                        <li>
                                            Automated deployment and continuous
                                            integration platform
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Cloudflare</strong>
                                    <ul>
                                        <li>
                                            Leading, global cloud platform that
                                            improves website performance,
                                            security, and reliability by acting
                                            as a reverse proxy between users and
                                            a site’s hosting provider
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Performance Optimization</strong>
                                    <ul>
                                        <li>
                                            Maximizing site speed, load times,
                                            and overall user experience
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Analytics & Reporting</strong>
                                    <ul>
                                        <li>
                                            Data-driven insights to measure
                                            success and guide improvements
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Automation</strong>
                                    <ul>
                                        <li>
                                            Streamlining workflows and reducing
                                            manual tasks with smart automation
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                        <div
                            class="services-tabs__panel"
                            id="v-pills-agentic"
                            role="tabpanel"
                            aria-labelledby="v-pills-agentic-tab"
                            data-tab-panel="v-pills-agentic"
                            hidden
                        >
                            <h3>Agentic Engineering:</h3>
                            <ul class="custom-list">
                                <li>
                                    <strong>Claude Code</strong>
                                    <ul>
                                        <li>
                                            Anthropic's agentic coding assistant
                                            for terminal-based development
                                            workflows
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Codex</strong>
                                    <ul>
                                        <li>
                                            OpenAI's code generation model
                                            powering intelligent code completion
                                            and synthesis
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Cursor</strong>
                                    <ul>
                                        <li>
                                            AI-first code editor with native LLM
                                            integration for pair programming
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Zed</strong>
                                    <ul>
                                        <li>
                                            High-performance collaborative
                                            editor with built-in AI assistance
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>VS Code</strong>
                                    <ul>
                                        <li>
                                            Industry-standard IDE enhanced with
                                            AI extensions and copilot
                                            integrations
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Kilo Code</strong>
                                    <ul>
                                        <li>
                                            Lightweight agentic coding tool for
                                            rapid prototyping and iteration
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Verdant</strong>
                                    <ul>
                                        <li>
                                            Next-gen development environment
                                            with autonomous code generation
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Copilot</strong>
                                    <ul>
                                        <li>
                                            GitHub's AI pair programmer offering
                                            real-time code suggestions
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Gemini</strong>
                                    <ul>
                                        <li>
                                            Google's multimodal AI for code
                                            understanding and generation
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Antigravity</strong>
                                    <ul>
                                        <li>
                                            Experimental AI development platform
                                            for pushing boundaries
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Builder.io &mdash; Fusion</strong>
                                    <ul>
                                        <li>
                                            Visual AI-powered design-to-code
                                            platform for rapid development
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Grok</strong>
                                    <ul>
                                        <li>
                                            xAI's conversational AI with
                                            real-time knowledge and coding
                                            capabilities
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Z.ai &mdash; GLM</strong>
                                    <ul>
                                        <li>
                                            Zhipu's generative language model
                                            for multilingual code assistance
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Kimi K2</strong>
                                    <ul>
                                        <li>
                                            Moonshot AI's long-context model for
                                            complex codebase understanding
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Qwen</strong>
                                    <ul>
                                        <li>
                                            Alibaba's open-weight model
                                            excelling at code generation tasks
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Nano Banana</strong>
                                    <ul>
                                        <li>
                                            Compact AI model optimized for edge
                                            deployment and local inference
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>ElevenLabs</strong>
                                    <ul>
                                        <li>
                                            AI voice synthesis for creating
                                            natural audio from generated content
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Veo 3</strong>
                                    <ul>
                                        <li>
                                            Google's video generation AI for
                                            creating cinematic content from
                                            prompts
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Sora 2</strong>
                                    <ul>
                                        <li>
                                            OpenAI's advanced video generation
                                            model for photorealistic content
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Neural Frames</strong>
                                    <ul>
                                        <li>
                                            AI-powered music video and animation
                                            generation platform
                                        </li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>RunwayML</strong>
                                    <ul>
                                        <li>
                                            Creative AI suite for video editing,
                                            generation, and visual effects
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <!-- //Tabs -->

                <div class="d-grid gap-2 col-6 mx-auto">
                    <a href="/work" class="btn btn-lg btn-custom" type="button"
                        >view work</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <!-- <div class="scroll-indicator">↓ scroll to sail ↓</div> -->

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>
<!-- Water Ripple CTA -->
